import { motion } from "framer-motion";
import {
    ABOUT_INTERIOR_URL,
    ORDER_ONLINE_URL,
    MONOGRAM_URL,
    cdnImg,
} from "../lib/data";
import ContactBar from "../components/ContactBar";

// Seamless infinite vertical marquee for the page edges. Uses a pure CSS
// keyframe animation (GPU-accelerated via translate3d) for buttery-smooth
// motion — framer-motion is overkill for long simple loops. The flex
// container stacks spans top-to-bottom; each span uses vertical-rl writing
// mode so its text reads downward. Doubling the items and animating from
// 0 to -50% means the second copy seamlessly takes over where the first
// ends — no visible seam.
function VerticalMarquee({ side = "right", direction = "up" }) {
    const phrase = "Albany · Burgers · Shakes ·";
    const items = Array.from({ length: 30 }).map((_, i) => (
        <span
            key={i}
            className="block font-display uppercase text-[#EB4A18]/[0.14] md:text-[#EB4A18]/[0.09] text-[clamp(2rem,7vw,5rem)] tracking-[0.18em] leading-none whitespace-nowrap"
            style={{ writingMode: "vertical-rl" }}
        >
            {phrase}
        </span>
    ));
    const positionClass =
        side === "left" ? "left-0 md:left-2" : "right-0 md:right-2";
    const animationName =
        direction === "up" ? "marqueeUp" : "marqueeDown";
    return (
        <div
            aria-hidden="true"
            className={`hidden md:block absolute ${positionClass} top-0 bottom-0 w-[44px] sm:w-[60px] lg:w-[90px] overflow-hidden pointer-events-none select-none`}
        >
            <div
                className="flex flex-col gap-6 md:gap-8"
                style={{
                    animation: `${animationName} 320s linear infinite`,
                    willChange: "transform",
                    transform: "translateZ(0)",
                }}
            >
                {items}
                {items}
            </div>
            <style>{`
                @keyframes marqueeUp {
                    0%   { transform: translate3d(0, 0, 0); }
                    100% { transform: translate3d(0, -50%, 0); }
                }
                @keyframes marqueeDown {
                    0%   { transform: translate3d(0, -50%, 0); }
                    100% { transform: translate3d(0, 0, 0); }
                }
            `}</style>
        </div>
    );
}

// Per-character reveal for the H1 — adds a subtle editorial flourish on desktop.
function HeroHeading() {
    const lines = [
        { text: "Our", color: "text-[#121212]" },
        { text: "Story.", color: "text-[#EB4A18]" },
    ];
    let charIdx = 0;
    return (
        <h1 className="font-display uppercase text-[clamp(2.4rem,6vw,5rem)] leading-[0.93]">
            {lines.map((line, li) => (
                <span key={li} className="block">
                    {Array.from(line.text).map((ch) => {
                        const i = charIdx++;
                        return (
                            <motion.span
                                key={`${li}-${i}`}
                                initial={{ opacity: 0, y: 40, rotate: -2 }}
                                animate={{ opacity: 1, y: 0, rotate: 0 }}
                                transition={{
                                    duration: 0.85,
                                    delay: 0.1 + i * 0.045,
                                    ease: [0.19, 1, 0.22, 1],
                                }}
                                className={`inline-block ${line.color}`}
                            >
                                {ch === " " ? "\u00A0" : ch}
                            </motion.span>
                        );
                    })}
                </span>
            ))}
        </h1>
    );
}

export default function AboutPage() {
    return (
        <>
            <section
                data-testid="about-page-section"
                className="relative pt-32 md:pt-36 pb-20 md:pb-28 overflow-hidden"
            >
                {/* DESKTOP-ONLY decorative vertical marquees on BOTH sides */}
                <VerticalMarquee side="left" direction="down" />
                <VerticalMarquee side="right" direction="up" />

                <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-6">
                            <motion.img
                                src={MONOGRAM_URL}
                                alt=""
                                aria-hidden="true"
                                initial={{ opacity: 0, y: 16, rotate: -8 }}
                                animate={{ opacity: 1, y: 0, rotate: 0 }}
                                transition={{
                                    duration: 0.9,
                                    ease: [0.19, 1, 0.22, 1],
                                }}
                                className="w-16 md:w-20 mb-6 select-none pointer-events-none"
                            />
                            <motion.p
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.08, duration: 0.7 }}
                                className="overline mb-3 !text-[#121212]"
                            >
                                About
                            </motion.p>

                            {/* DESKTOP — per-character animated heading */}
                            <div className="hidden md:block">
                                <HeroHeading />
                            </div>

                            {/* MOBILE — simple heading */}
                            <h1 className="md:hidden font-display uppercase text-[#121212] text-[clamp(2.4rem,6vw,5rem)] leading-[0.93]">
                                Our
                                <br />
                                <span className="text-[#EB4A18]">Story.</span>
                            </h1>

                            {/* Body paragraphs — desktop gets per-paragraph stagger with a subtle accent line */}
                            <div className="mt-8 space-y-5 text-[#4A4A4A] text-base md:text-lg leading-relaxed max-w-xl">
                                {[
                                    "We at The Gaffer Burgers & Shakes, believe your burger should be as feel-good as it is delicious!",
                                    "That's why we use only the best, sustainably sourced produce and 100% hormone-free, grass-fed beef from our trusted local legends.",
                                    "We support those who grow and deliver the freshest ingredients, so every bite is bursting with flavour and good vibes!",
                                ].map((p, i) => (
                                    <motion.p
                                        key={i}
                                        initial={{ opacity: 0, y: 18 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, amount: 0.3 }}
                                        transition={{
                                            delay: 0.4 + i * 0.12,
                                            duration: 0.8,
                                            ease: [0.19, 1, 0.22, 1],
                                        }}
                                    >
                                        {p}
                                    </motion.p>
                                ))}
                            </div>

                            {/* Decorative accent line that draws on enter (desktop only) */}
                            <motion.span
                                aria-hidden="true"
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{
                                    delay: 0.9,
                                    duration: 0.9,
                                    ease: [0.19, 1, 0.22, 1],
                                }}
                                className="hidden md:block mt-8 h-[2px] w-24 bg-[#EB4A18] origin-left"
                            />

                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.95, duration: 0.9 }}
                                className="mt-10"
                            >
                                <a
                                    href={ORDER_ONLINE_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="about-order-online-btn"
                                    className="btn-primary"
                                >
                                    Order Online
                                    <span aria-hidden="true">→</span>
                                </a>
                            </motion.div>
                        </div>

                        {/* RIGHT — image (appears immediately, no transition) */}
                        <figure className="lg:col-span-6 relative">
                            <div className="relative overflow-hidden">
                                <img
                                    src={cdnImg(ABOUT_INTERIOR_URL, 1200)}
                                    alt="Inside The Gaffer Burgers & Shakes — neon green Gaffer sign over the counter, bar stools and pendant lights"
                                    loading="eager"
                                    fetchpriority="high"
                                    decoding="async"
                                    className="w-full h-auto object-cover aspect-square"
                                />
                            </div>

                            {/* Decorative photo "tag" sticker — desktop only */}
                            <motion.span
                                aria-hidden="true"
                                initial={{ opacity: 0, y: 12, rotate: -8 }}
                                animate={{ opacity: 1, y: 0, rotate: -3 }}
                                transition={{
                                    delay: 0.6,
                                    duration: 0.8,
                                    ease: [0.19, 1, 0.22, 1],
                                }}
                                className="hidden md:inline-flex items-center gap-1.5 absolute -top-3 -left-3 px-3 py-1.5 bg-[#EB4A18] text-[#FAF8F4] text-[0.6rem] uppercase tracking-[0.22em] font-extrabold shadow-[0_8px_18px_-6px_rgba(0,0,0,0.4)]"
                            >
                                <span className="w-1 h-1 rounded-full bg-[#FAF8F4]" />
                                190 York St · Albany
                            </motion.span>
                        </figure>
                    </div>
                </div>
            </section>

            <ContactBar />
        </>
    );
}
